import { useCallback, useMemo, type MouseEvent, type ReactElement } from 'react';
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MenuInfo } from 'rc-menu/es/interface';
import type { MenuProps } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { PageLayout, useThemeMenuItem, type SideBarProps, type TopBarProps } from '@ohpen/enso-ui';

import { useConfigContext } from '@config/context';
import { visibleSections } from '@config/navigation';
import { ConfigRoutes } from '@config/routes';
import { isInternalReturnTo } from '@config/utils';
import { getSideBarItems } from './sideBarItems';

export interface ConfigLayoutProps {
  logoSrc: string;
  userMenu: TopBarProps['userMenu'];
  customMenu?: ReactElement;
  onClickMyAccount?: () => void;
}

export const ConfigLayout = ({ logoSrc, userMenu, customMenu, onClickMyAccount }: ConfigLayoutProps): ReactElement => {
  const { t } = useTranslation();
  const { applicationName, canViewSection, configuration, returnTo } = useConfigContext();

  // The mode lives in one place for the whole origin, so the choice made in the host still
  // holds here; calling the hook is also what applies it while configuration is on screen.
  const themeEnabled = Boolean(configuration?.featureToggles?.enableDarkMode);
  const themeMenuItem = useThemeMenuItem(themeEnabled);

  const userMenuExtraItems: MenuProps['items'] = useMemo(() => {
    const entries: MenuProps['items'] = [];
    if (themeEnabled) entries.push(themeMenuItem);
    if (onClickMyAccount) {
      entries.push({ key: 'my-account', icon: <UserOutlined />, label: t('config.myAccount'), onClick: onClickMyAccount });
    }

    return entries;
  }, [onClickMyAccount, t, themeEnabled, themeMenuItem]);
  const navigate = useNavigate();
  const location = useLocation();

  // Where the module is mounted, which is not a constant: it is one route of a host today and
  // the root of its own deployment later, and the side bar has to keep working in both.
  const basePath = useResolvedPath('.').pathname.replace(/\/$/, '');

  const selectedKey = useMemo(() => {
    const remainder = location.pathname.startsWith(basePath) ? location.pathname.slice(basePath.length + 1) : '';
    return visibleSections(canViewSection).find(section => section.path === remainder)?.path;
  }, [basePath, canViewSection, location.pathname]);

  const onClickItem = useCallback(({ key }: MenuInfo) => navigate(`${basePath}/${key}`), [basePath, navigate]);

  // Internal means the module is still mounted inside the host, so the way out is a route rather
  // than a page load. Anything else is an origin the host declared, and a real navigation.
  const isInternal = isInternalReturnTo(returnTo);

  const onClickLogo = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!isInternal) return;
      event.preventDefault();
      navigate(returnTo);
    },
    [isInternal, navigate, returnTo],
  );

  const sideBar: SideBarProps = useMemo(
    () => ({
      items: getSideBarItems(t, canViewSection),
      selectedKeys: selectedKey ? [selectedKey] : [],
      onClick: onClickItem,
      width: 220,
    }),
    [canViewSection, onClickItem, selectedKey, t],
  );

  const topBar: TopBarProps = useMemo(
    () => ({
      // The logo is the way out: configuration has no home of its own, you always land on a section.
      logo: { src: logoSrc, href: returnTo, onClick: onClickLogo },
      appName: `${applicationName} - ${t('config.appNameSuffix')}`,
      userMenu: { ...userMenu, extraItems: userMenuExtraItems },
      customMenu,
    }),
    [applicationName, customMenu, logoSrc, onClickLogo, returnTo, t, userMenu, userMenuExtraItems],
  );

  return (
    <PageLayout sideBar={sideBar} topBar={topBar}>
      <ConfigRoutes />
    </PageLayout>
  );
};
